import { useEffect, useRef } from 'react';
import { useClientConfig } from '../client/ClientConfigProvider';
import { useTokens } from '../../core/theme/TokensProvider';

const setLinkHref = (rel: string, href: string): void => {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
    }
    link.href = href;
};

const setMetaContent = (name: string, content: string): void => {
    let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
    }
    meta.content = content;
};

// Best-effort MIME inference from the URL extension. Returns undefined for
// unknown extensions so the manifest icon entry omits the `type` field —
// some PWA installers reject entries whose declared type doesn't match the
// actual asset, so guessing is worse than leaving it off.
const inferIconMime = (url: string): string | undefined => {
    const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'png': return 'image/png';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        case 'ico': return 'image/x-icon';
        case 'svg': return 'image/svg+xml';
        case 'webp': return 'image/webp';
        default: return undefined;
    }
};

const buildManifest = (
    name: string,
    primaryColor: string,
    iconUrl: string
): object => {
    if (!iconUrl) {
        return {
            short_name: name,
            name,
            icons: [],
            start_url: '.',
            display: 'standalone',
            theme_color: primaryColor,
            background_color: '#ffffff',
        };
    }
    const mime = inferIconMime(iconUrl);
    const icon: { src: string; sizes: string; type?: string } = {
        src: iconUrl,
        sizes: '192x192 512x512',
    };
    if (mime) icon.type = mime;
    return {
        short_name: name,
        name,
        icons: [icon],
        start_url: '.',
        display: 'standalone',
        theme_color: primaryColor,
        background_color: '#ffffff',
    };
};

export const BrandingApplier: React.FC = () => {
    // Asset/content branding (title, favicon, logo, club name) is read directly;
    // the theme colour comes from the tenant-resolved tokens so the colour
    // plumbing has a single source (FORT-180).
    const branding = useClientConfig().branding;
    const primaryColor = useTokens().color.primary;
    const manifestUrlRef = useRef<string | null>(null);

    useEffect(() => {
        if (branding.pageTitle) {
            document.title = branding.pageTitle;
        }

        const iconHref = branding.faviconUrl || branding.logoUrl;
        if (iconHref) {
            setLinkHref('icon', iconHref);
        }

        setMetaContent('theme-color', primaryColor);

        const manifest = buildManifest(
            branding.clubName || branding.pageTitle || 'Fortius Club',
            primaryColor,
            iconHref
        );
        const manifestBlob = new Blob([JSON.stringify(manifest)], {
            type: 'application/json',
        });
        const manifestUrl = URL.createObjectURL(manifestBlob);
        setLinkHref('manifest', manifestUrl);

        if (manifestUrlRef.current) {
            URL.revokeObjectURL(manifestUrlRef.current);
        }
        manifestUrlRef.current = manifestUrl;

        return () => {
            if (manifestUrlRef.current) {
                URL.revokeObjectURL(manifestUrlRef.current);
                manifestUrlRef.current = null;
            }
        };
    }, [
        branding.pageTitle,
        branding.faviconUrl,
        branding.logoUrl,
        primaryColor,
        branding.clubName,
    ]);

    return null;
};
